import './DatasetEdit.scss';

import { useCallback, useEffect, useState } from 'react'
import { Helmet } from "react-helmet";
import { Breadcrumb, Card, Container, Row } from 'react-bootstrap'
import { Link, useNavigate, useParams } from 'react-router-dom';
import { BdaDataset } from '../../models/BdaDataset';
import { Datasets } from '../../api/Datasets';
import AlidaLoader from '../../components/common/AlidaLoader';
import { useToasts } from '../../contexts/toasts';
import { Alert } from 'antd';
import AssetEditForm from '../../components/forms/AssetEditForm';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useSelector } from 'react-redux';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import { RootState } from '../../store';

export default function DatasetEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [dataset, setDataset] = useState<Partial<BdaDataset>>();
  const [error, setError] = useState(false);

  const { showLoader, hideLoader } = useFullScreenLoader();
  const { showToast } = useToasts();

  const assetLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "datasets"));

  const retrieveDataset = useCallback(() => {
    Datasets.getDataset(+id!)
      .then(
        ({ data }) => setDataset(data),
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('dataset-error', message, `${assetLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [id, showToast, assetLabels]);

  useEffect(() => {
    if (id) retrieveDataset();
    else setDataset(new BdaDataset());
  }, [id, retrieveDataset]);

  const onCancel = useCallback(() => navigate('/datasets'), [navigate]);

  const submitDataset = useCallback((body: BdaDataset) => {
    showLoader();

    const promise = id ? Datasets.updateDataset(body) : Datasets.createDataset(body);

    promise.then(
      ({ data }) => {
        hideLoader();
        showToast('create-dataset-success', `${assetLabels.singular} ${id ? 'edited' : 'registered'} successfully.`, `${assetLabels.singular} Success`, 'success');
        navigate(`/datasets/${data.id}`);
      },
      () => {
        hideLoader();
        showToast('create-dataset-error', `Could not ${id ? 'edit' : 'register'} ${assetLabels.singular}.`, `${assetLabels.singular} Error`, 'danger');
      },
    );
  }, [id, showLoader, hideLoader, navigate, showToast, assetLabels]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert
        type="error"
        showIcon
        message={`An error occurred while retrieving ${assetLabels.singular}. Try again later.`}
      />
    );

    return dataset
      ? <AssetEditForm mode='dataset' asset={dataset} modeLabel={assetLabels.singular || "Dataset"} onCancel={onCancel} onSubmit={submitDataset} />
      : (
        <div className="p-5 text-center">
          <AlidaLoader />
        </div>
      );
  }, [error, dataset, onCancel, submitDataset, assetLabels]);

  return (
    <Container className="my-3">
      <Helmet>
        <title>{assetLabels.singular} {id ? `${id} Edit` : "Registration"}</title>
      </Helmet>

      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/datasets' }}>{assetLabels.plural}</Breadcrumb.Item>
        {id
          ? (
            <>
              <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/datasets/${id}` }}>{dataset?.name}</Breadcrumb.Item>
              <Breadcrumb.Item active>Edit</Breadcrumb.Item>
            </>
          )
          : <Breadcrumb.Item active>New {assetLabels.singular}</Breadcrumb.Item>
        }
      </Breadcrumb>

      <Card>
        <Row className="gx-0 border-bottom p-2 px-3">
          <h5 className="mb-1">{id ? 'Edit' : 'New'} {assetLabels.singular}</h5>
          <h6 className="text-muted mb-0">{id ? `Edit existing ${assetLabels.singular} fields.` : `Fill in desired ${assetLabels.singular} details.`}</h6>
        </Row>

        {renderAsyncContent()}
      </Card>
    </Container>
  )
}